/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
  WritableSignerAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import type { WhirlpoolBumps, WhirlpoolBumpsArgs } from "../types/index.js";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  getU128Decoder,
  getU128Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";
import {
  getWhirlpoolBumpsDecoder,
  getWhirlpoolBumpsEncoder,
} from "../types/index.js";

export const INITIALIZE_POOL_DISCRIMINATOR: ReadonlyUint8Array = new Uint8Array(
  [95, 180, 10, 172, 84, 174, 232, 40],
);

export function getInitializePoolDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_POOL_DISCRIMINATOR,
  );
}

export type InitializePoolInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountWhirlpoolsConfig extends string | AccountMeta = string,
  TAccountTokenMintA extends string | AccountMeta = string,
  TAccountTokenMintB extends string | AccountMeta = string,
  TAccountFunder extends string | AccountMeta = string,
  TAccountWhirlpool extends string | AccountMeta = string,
  TAccountTokenVaultA extends string | AccountMeta = string,
  TAccountTokenVaultB extends string | AccountMeta = string,
  TAccountFeeTier extends string | AccountMeta = string,
  TAccountTokenProgram extends
    | string
    | AccountMeta = "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TAccountSystemProgram extends
    | string
    | AccountMeta = "11111111111111111111111111111111",
  TAccountRent extends
    | string
    | AccountMeta = "SysvarRent111111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountWhirlpoolsConfig extends string
        ? ReadonlyAccount<TAccountWhirlpoolsConfig>
        : TAccountWhirlpoolsConfig,
      TAccountTokenMintA extends string
        ? ReadonlyAccount<TAccountTokenMintA>
        : TAccountTokenMintA,
      TAccountTokenMintB extends string
        ? ReadonlyAccount<TAccountTokenMintB>
        : TAccountTokenMintB,
      TAccountFunder extends string
        ? WritableSignerAccount<TAccountFunder> &
            AccountSignerMeta<TAccountFunder>
        : TAccountFunder,
      TAccountWhirlpool extends string
        ? WritableAccount<TAccountWhirlpool>
        : TAccountWhirlpool,
      TAccountTokenVaultA extends string
        ? WritableSignerAccount<TAccountTokenVaultA> &
            AccountSignerMeta<TAccountTokenVaultA>
        : TAccountTokenVaultA,
      TAccountTokenVaultB extends string
        ? WritableSignerAccount<TAccountTokenVaultB> &
            AccountSignerMeta<TAccountTokenVaultB>
        : TAccountTokenVaultB,
      TAccountFeeTier extends string
        ? ReadonlyAccount<TAccountFeeTier>
        : TAccountFeeTier,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      TAccountRent extends string
        ? ReadonlyAccount<TAccountRent>
        : TAccountRent,
      ...TRemainingAccounts,
    ]
  >;

export interface InitializePoolInstructionData {
  discriminator: ReadonlyUint8Array;
  bumps: WhirlpoolBumps;
  tickSpacing: number;
  initialSqrtPrice: bigint;
}

export interface InitializePoolInstructionDataArgs {
  bumps: WhirlpoolBumpsArgs;
  tickSpacing: number;
  initialSqrtPrice: number | bigint;
}

export function getInitializePoolInstructionDataEncoder(): FixedSizeEncoder<InitializePoolInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["bumps", getWhirlpoolBumpsEncoder()],
      ["tickSpacing", getU16Encoder()],
      ["initialSqrtPrice", getU128Encoder()],
    ]),
    (value) => ({ ...value, discriminator: INITIALIZE_POOL_DISCRIMINATOR }),
  );
}

export function getInitializePoolInstructionDataDecoder(): FixedSizeDecoder<InitializePoolInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["bumps", getWhirlpoolBumpsDecoder()],
    ["tickSpacing", getU16Decoder()],
    ["initialSqrtPrice", getU128Decoder()],
  ]);
}

export function getInitializePoolInstructionDataCodec(): FixedSizeCodec<
  InitializePoolInstructionDataArgs,
  InitializePoolInstructionData
> {
  return combineCodec(
    getInitializePoolInstructionDataEncoder(),
    getInitializePoolInstructionDataDecoder(),
  );
}

export interface InitializePoolInput<
  TAccountWhirlpoolsConfig extends string = string,
  TAccountTokenMintA extends string = string,
  TAccountTokenMintB extends string = string,
  TAccountFunder extends string = string,
  TAccountWhirlpool extends string = string,
  TAccountTokenVaultA extends string = string,
  TAccountTokenVaultB extends string = string,
  TAccountFeeTier extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountRent extends string = string,
> {
  whirlpoolsConfig: Address<TAccountWhirlpoolsConfig>;
  tokenMintA: Address<TAccountTokenMintA>;
  tokenMintB: Address<TAccountTokenMintB>;
  funder: TransactionSigner<TAccountFunder>;
  whirlpool: Address<TAccountWhirlpool>;
  tokenVaultA: TransactionSigner<TAccountTokenVaultA>;
  tokenVaultB: TransactionSigner<TAccountTokenVaultB>;
  feeTier: Address<TAccountFeeTier>;
  tokenProgram?: Address<TAccountTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  rent?: Address<TAccountRent>;
  bumps: InitializePoolInstructionDataArgs["bumps"];
  tickSpacing: InitializePoolInstructionDataArgs["tickSpacing"];
  initialSqrtPrice: InitializePoolInstructionDataArgs["initialSqrtPrice"];
}

export function getInitializePoolInstruction<
  TAccountWhirlpoolsConfig extends string,
  TAccountTokenMintA extends string,
  TAccountTokenMintB extends string,
  TAccountFunder extends string,
  TAccountWhirlpool extends string,
  TAccountTokenVaultA extends string,
  TAccountTokenVaultB extends string,
  TAccountFeeTier extends string,
  TAccountTokenProgram extends string,
  TAccountSystemProgram extends string,
  TAccountRent extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: InitializePoolInput<
    TAccountWhirlpoolsConfig,
    TAccountTokenMintA,
    TAccountTokenMintB,
    TAccountFunder,
    TAccountWhirlpool,
    TAccountTokenVaultA,
    TAccountTokenVaultB,
    TAccountFeeTier,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >,
  config?: { programAddress?: TProgramAddress },
): InitializePoolInstruction<
  TProgramAddress,
  TAccountWhirlpoolsConfig,
  TAccountTokenMintA,
  TAccountTokenMintB,
  TAccountFunder,
  TAccountWhirlpool,
  TAccountTokenVaultA,
  TAccountTokenVaultB,
  TAccountFeeTier,
  TAccountTokenProgram,
  TAccountSystemProgram,
  TAccountRent
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    whirlpoolsConfig: {
      value: input.whirlpoolsConfig ?? null,
      isWritable: false,
    },
    tokenMintA: { value: input.tokenMintA ?? null, isWritable: false },
    tokenMintB: { value: input.tokenMintB ?? null, isWritable: false },
    funder: { value: input.funder ?? null, isWritable: true },
    whirlpool: { value: input.whirlpool ?? null, isWritable: true },
    tokenVaultA: { value: input.tokenVaultA ?? null, isWritable: true },
    tokenVaultB: { value: input.tokenVaultB ?? null, isWritable: true },
    feeTier: { value: input.feeTier ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    rent: { value: input.rent ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.rent.value) {
    accounts.rent.value =
      "SysvarRent111111111111111111111111111111111" as Address<"SysvarRent111111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("whirlpoolsConfig", accounts.whirlpoolsConfig),
      getAccountMeta("tokenMintA", accounts.tokenMintA),
      getAccountMeta("tokenMintB", accounts.tokenMintB),
      getAccountMeta("funder", accounts.funder),
      getAccountMeta("whirlpool", accounts.whirlpool),
      getAccountMeta("tokenVaultA", accounts.tokenVaultA),
      getAccountMeta("tokenVaultB", accounts.tokenVaultB),
      getAccountMeta("feeTier", accounts.feeTier),
      getAccountMeta("tokenProgram", accounts.tokenProgram),
      getAccountMeta("systemProgram", accounts.systemProgram),
      getAccountMeta("rent", accounts.rent),
    ],
    data: getInitializePoolInstructionDataEncoder().encode(
      args as InitializePoolInstructionDataArgs,
    ),
    programAddress,
  } as InitializePoolInstruction<
    TProgramAddress,
    TAccountWhirlpoolsConfig,
    TAccountTokenMintA,
    TAccountTokenMintB,
    TAccountFunder,
    TAccountWhirlpool,
    TAccountTokenVaultA,
    TAccountTokenVaultB,
    TAccountFeeTier,
    TAccountTokenProgram,
    TAccountSystemProgram,
    TAccountRent
  >);
}

export interface ParsedInitializePoolInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    whirlpoolsConfig: TAccountMetas[0];
    tokenMintA: TAccountMetas[1];
    tokenMintB: TAccountMetas[2];
    funder: TAccountMetas[3];
    whirlpool: TAccountMetas[4];
    tokenVaultA: TAccountMetas[5];
    tokenVaultB: TAccountMetas[6];
    feeTier: TAccountMetas[7];
    tokenProgram: TAccountMetas[8];
    systemProgram: TAccountMetas[9];
    rent: TAccountMetas[10];
  };
  data: InitializePoolInstructionData;
}

export function parseInitializePoolInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializePoolInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 11) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 11,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      whirlpoolsConfig: getNextAccount(),
      tokenMintA: getNextAccount(),
      tokenMintB: getNextAccount(),
      funder: getNextAccount(),
      whirlpool: getNextAccount(),
      tokenVaultA: getNextAccount(),
      tokenVaultB: getNextAccount(),
      feeTier: getNextAccount(),
      tokenProgram: getNextAccount(),
      systemProgram: getNextAccount(),
      rent: getNextAccount(),
    },
    data: getInitializePoolInstructionDataDecoder().decode(instruction.data),
  };
}
